// @flow

import React, { useState } from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';

import { WebsiteDefaultPage as BaseWebsiteDefaultPage } from 'components/Page';
import { PageType } from 'Constants';
import { OriginationFees } from 'data/global/footer/legal/numbered';
import { useImages } from 'hooks';
import { Color, flex, Layout, maxWidthContainer, topBottomMargin, Width } from 'styles';
import { MarketsHero, Toggle } from 'components/Content';
import { Button, ImageLoader } from 'components/Gui';

const METADATA = {
  title: 'Financial products to simplify finances, faster',
  description: `At Markets Direct, we’re transforming home equity by creating a fast, simple and transparent process to bring you closer to your financial goals. We'll show you how to use home equity to consolidate debt, pay for home improvement or fund your retirement. It's quick and easy. Let’s unlock the potential of your largest asset.`,
};

const PAGE_TYPE = {
  product: PageType.PRODUCT.NONE,
};

const WebsiteDefaultPage = styled(BaseWebsiteDefaultPage)`
  width: 100% !important;
`;

const Wrapper = styled.div`
  width: 100%;
  ${flex()};
  flex-direction: column;
  ${topBottomMargin([Layout.NAV_HEIGHT_MOBILE, 0], [Layout.NAV_HEIGHT_DESKTOP, 0], [Layout.NAV_HEIGHT_DESKTOP, 0])};
  position: relative;

  > div.image {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 370px;
  }
`;

const Products = styled.div`
  width: 100%;
  background: white;
  padding-top: 60px;
  padding-bottom: 200px;

  @media (min-width: ${Width.BREAK_MD}px) {
    padding-bottom: 80px;
  }

  > div {
    padding: 40px 0;
    ${maxWidthContainer(1080)};
    ${flex()};
  }
`;

const Buttons = styled.div`
  ${flex('flex-start', 'flex-start')};
  flex-direction: column;
`;

const Flex = styled.div`
  ${flex('flex-start', 'flex-start')};
  flex-direction: column;
  margin-bottom: 60px;

  @media (min-width: ${Width.BREAK_MD}px) {
    flex-direction: row;
    font-size: 3rem;
  }
`;

const ContentData = [
  {
    image: 'forex',
    title: 'Forex',
    text: (
      <Flex>
        <div>
          <p>
            The Forex market is far and away the largest traded market in the world, with an estimated daily turnover of $7 trillion,
            vs. 84 billion in equities worldwide.
          </p>
          <ul>
            <li>
              <span>></span>Take advantage of global trading opportunities in over 70 currency and metals pairs.
            </li>
            <li>
              <span>></span>24 hours. 5 days. The Forex market never sleeps
            </li>
            <li>
              <span>></span>Trade all markets, rising or falling.
            </li>
            <li>
              <span>></span>Hedge your positions in the Futures exchange
            </li>
          </ul>
          <Buttons>
            <Button url="/fx" className="button">
              View Spreads
            </Button>
          </Buttons>
        </div>
      </Flex>
    ),
  },
  {
    image: 'futures',
    title: 'Futures',
    text: (
      <Flex>
        <div>
          <p>
            Built upon the acclaimed CQG Integrated Client desktop with its extensive market data feeds, advanced technical analysis,
            charting, and order management tools.
          </p>
          <ul>
            <li>
              <span>></span>Trade or hedge over 60 futures products virtually 24 hours a day
            </li>
            <li>
              <span>></span>Diversify with metals, energies, agricultures, interest rates, indices, and more.
            </li>
            <li>
              <span>></span>Direct market access to more than 40 exchanges worldwide
            </li>
            <li>
              <span>></span>Hedge or speculate - the choice is yours
            </li>
          </ul>
        </div>
      </Flex>
    ),
  },
  {
    image: 'equities',
    title: 'US Equities',
    text: (
      <Flex>
        <div>
          <p>
            Markets Direct has partnered with DriveWealth, LLC to offer our clients a simple, low cost solution to invest in US
            equities.
          </p>
          <ul>
            <li>
              <span>></span>Zero Commission Trading
            </li>
            <li>
              <span>></span>Access to over 3500 stocks and ETF’s on the NYSE and NASDAQ exchanges
            </li>
            <li>
              <span>></span>Open your account in a highly regulated, transparent and secure US brokerage account
            </li>
          </ul>
        </div>
      </Flex>
    ),
  },
  {
    image: 'etf',
    title: 'ETFs',
    text: (
      <Flex>
        <div>
          <p>
            Exchange Traded Funds (ETF’s) are investment funds that trade on exchanges much like stocks. Invest in a basket of
            securities that are highly liquid and transparent.
          </p>
          <ul>
            <li>
              <span>></span>Exposure to indices, sectors and commodities $0 commissions
            </li>
            <li>
              <span>></span>Diverse opportunities. ETF’s invest in stocks, bonds, commodities, currencies, interest rates and
              volatility.
            </li>
            <li>
              <span>></span>Provides diversification to help balance risk with passively managed strategies.
            </li>
          </ul>
        </div>
      </Flex>
    ),
  },
];

const PageContainer = ({ data }: { data: any }) => {
  const getImage = useImages(data);
  const [index, setIndex] = useState(0);
  return (
    <WebsiteDefaultPage
      page={PAGE_TYPE}
      metaData={{
        ...METADATA,
        image: getImage('products-v2'),
      }}
      footerLegal={{
        numbered: OriginationFees,
      }}>
      <Wrapper>
        <div className="image">
          <ImageLoader image={getImage(ContentData[index].image)} />
        </div>
        <MarketsHero
          title="PRODUCTS"
          blurb="A wide cast of instruments for any strategy"
          image={{
            name: 'products-v2',
          }}
          images={data}
        />
        <Products>
          <div>
            <Toggle setIndex={setIndex} content={ContentData} />
          </div>
        </Products>
      </Wrapper>
    </WebsiteDefaultPage>
  );
};

export default PageContainer;

export const query = graphql`
  query {
    imageToWebP: allFile(
      filter: { relativeDirectory: { in: ["pages/products", "pages/resources"] }, extension: { nin: ["svg", "gif"] } }
    ) {
      edges {
        node {
          ...ImageToWebP
        }
      }
    }
    otherImage: allFile(filter: { relativeDirectory: { eq: "pages/products" }, extension: { in: ["svg", "gif"] } }) {
      edges {
        node {
          ...OtherImage
        }
      }
    }
  }
`;
